import { useEffect, useState, type ReactNode } from 'react';
import { useInterval } from 'usehooks-ts';
import { useLocation, useSearchParams } from 'react-router-dom';

import './Header.scss';

import aquaLogo from '../../assets/aqua-logo.svg';
import { useGetDealerBranding } from '../../queries/useGetDealerBranding.ts';
import { useGetFlowTemplateById } from '../../queries/useGetFlowTemplateById.ts';
import { NavLinkProp } from '../../types.ts';
import config from '../../utils/feature-flags.json';
import isLoggedIn from '../../utils/isLoggedIn.ts';
import { returnImgSrc } from '../../utils/returnImgSrc.ts';
import NavigationLink from '../NavigationLink/NavigationLink';
import { useGetDealerIdByLoanApplicationId } from '../../queries/useGetDealerIdByLoanApplicationId.ts';
import { ALTERNATE_LOGO_KEY } from '../../utils/constants/branding';

interface HeaderProps {
  navigationLinks?: NavLinkProp[];
  portal?: string;
  extraNavContent?: ReactNode;
}

const Header = ({ navigationLinks, portal, extraNavContent }: HeaderProps) => {
  const [showLinks, setShowLinks] = useState(false);
  const [searchParams] = useSearchParams();
  const [dealerID, setDealerID] = useState('');
  const [consumerOnly, setConsumerOnly] = useState(false);
  const { data: dealerIdFromLoanId } = useGetDealerIdByLoanApplicationId(
    localStorage.getItem('loan_application_id') || '',
  );
  const { data } = useGetDealerBranding(localStorage.getItem('dealer_id') || dealerID);
  const { data: templateData } = useGetFlowTemplateById(data?.flow_template_id || '');
  const { pathname } = useLocation();
  const hideLinks =
    sessionStorage.getItem('is_standalone_dealer') === 'false' ||
    pathname === '/accept_invite' ||
    pathname === '/password_reset';
  const isConsumerPortal = portal === 'consumer' || window.location.hostname.includes('consumer');

  useEffect(() => {
    for (const entry of searchParams.entries()) {
      const [param, value] = entry;
      const parameter = param;

      if (parameter === 'dealer_id') {
        localStorage.setItem('dealer_id', decodeURI(value));
      }

      if (parameter === 'loan_application_id') {
        localStorage.setItem('loan_application_id', decodeURI(value));
      }
    }
  }),
    [searchParams];

  useEffect(() => {
    !!dealerIdFromLoanId && dealerID === '' && setDealerID(dealerIdFromLoanId);
  }, [dealerIdFromLoanId]);

  useEffect(() => {
    templateData !== undefined && setConsumerOnly(templateData.consumer_only);
    templateData !== undefined && localStorage.setItem('flow_template', JSON.stringify(templateData));
    return;
  }, [templateData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useInterval(() => {
    setShowLinks(isLoggedIn() && !(sessionStorage.getItem('is_standalone_dealer') === 'false'));
  }, 500);

  const alternateLogo = localStorage.getItem(ALTERNATE_LOGO_KEY);
  const storedAlternateLogo = alternateLogo && alternateLogo.trim().length > 0 ? alternateLogo.trim() : undefined;

  const returnLogo = () => {
    if (storedAlternateLogo) {
      return (
        <img src={`${returnImgSrc(storedAlternateLogo)}`} aria-label="Dealer logo" alt="Dealer logo" width="300px" />
      );
    }

    // remove this when OEM feature is permanently enabled
    if (!config.FF_OEMTemplatesEnabled && data?.dealer_logo_base64) {
      return (
        <img
          src={`${returnImgSrc((templateData?.primary_logo || data?.dealer_logo_base64) ?? '')}`}
          aria-label="Dealer logo"
          alt="Dealer logo"
          width="300px"
        />
      );
    }

  if (config.FF_OEMTemplatesEnabled && templateData?.primary_logo) {
    if (!consumerOnly) {
      const logoSource = templateData?.primary_logo ?? data?.dealer_logo_base64 ?? '';
      return logoSource ? (
        <img src={`${returnImgSrc(logoSource)}`} aria-label="Dealer logo" alt="Dealer logo" width="300px" />
      ) : (
        <img src={aquaLogo} aria-label="Aqua logo" alt="Aqua logo" width="300px" />
      );
    } else if (consumerOnly && portal === 'consumer') {
      return null;
    }
  }

    return <img src={aquaLogo} aria-label="Aqua logo" alt="Aqua logo" />;
  };

  return (
    <header className="header-container">
      {!isConsumerPortal && returnLogo()}
      <nav>
        {showLinks && !hideLinks && extraNavContent}
        {showLinks &&
          !hideLinks &&
          navigationLinks?.map((link, index) => {
            return (
              <NavigationLink
                label={link.label}
                path={link.url}
                key={index}
                className={index === 0 && !extraNavContent ? '' : 'border-left'}
              />
            );
          })}
      </nav>
    </header>
  );
};

export default Header;
